import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { General, Time } from '@poseidon/components/StrokeIcons';

const MIN_SCALE = 0.3;
const MAX_SCALE = 2.5;
/** The smallest an auto-fit-on-mount will shrink the graph — below this the labels stop being readable. */
const MOUNT_FIT_MIN_SCALE = 0.7;
const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

/** The dot grid's spacing at 100%, in canvas units. */
const DOT_STEP = 24;
/** The closest two dots may sit on screen — zoomed out past this, the grid thins instead of packing tighter. */
const MIN_DOT_GAP_PX = 16;

/**
 * The dot grid at a zoom level, in screen px. It lives on the canvas, so it zooms with the content: zoomed
 * out, the dots shrink with the nodes (down to a hairline) and the grid keeps every 2nd / 4th / … dot once
 * they'd sit closer than {@link MIN_DOT_GAP_PX} — a fixed-size dot on a 7px pitch turns the canvas into a grey
 * haze that dwarfs the graph. Zoomed in, a dot never grows past its 100% size. `offsetPx` keeps a thinned
 * grid a SUBSET of the full one (dots drop out as you zoom out; they don't jump).
 */
function dotGridAt(scale: number): { gapPx: number; radiusPx: number; offsetPx: number } {
  const fullGapPx = DOT_STEP * scale;
  let gapPx = fullGapPx;
  while (gapPx < MIN_DOT_GAP_PX) gapPx *= 2;
  return { gapPx, radiusPx: Math.min(1, Math.max(0.5, scale)), offsetPx: (fullGapPx - gapPx) / 2 };
}

interface View {
  x: number;
  y: number;
  scale: number;
}

const INITIAL: View = { x: 48, y: 32, scale: 1 };

/** With `boundPan`, the breathing room kept between the content's edge and the viewport's (screen px). */
const PAN_EDGE_PX = 24;
/** Where `focusSelector`'s element lands on open / on "Now": a little left of centre (room for the cards
 *  and previews that sit to the RIGHT of the spine), and a little above middle (room for what comes next). */
const FOCUS_AT = { x: 0.38, y: 0.42 };
/** Elements that belong WITH the focus point (the NOW marker beside the node): the opening frame keeps them
 *  on screen too, so a wide marker on a narrow canvas isn't cut off at the edge. */
const FOCUS_EXTRA_SELECTOR = '[data-canvas-focus-extra]';
/** The least gap kept between the focus group and the viewport's edge (screen px). */
const FOCUS_EDGE_PX = 12;

interface PanZoomProps {
  children: ReactNode;
  /**
   * Whether a plain wheel / two-finger gesture PANS the canvas (the default, for a viewport that owns the
   * whole page). Set false when the viewport is embedded in a scrolling page: panning swallows the wheel
   * (it `preventDefault`s), so a tall canvas becomes a scroll trap — the page stops moving as soon as the
   * cursor crosses it, and anything below it is unreachable. With it off the wheel scrolls the page as
   * usual and the canvas is still panned by dragging and zoomed with Z / Ctrl / ⌘ + scroll.
   */
  wheelPan?: boolean;
  /**
   * Open already framed on the whole graph instead of at 100% in the top-left corner. For a READ-ONLY
   * canvas — a contact's locked sequence — the first frame is the whole point: a branch lane sits far to the
   * right of the spine, so at 100% the interesting half is off-screen until you think to pan. Never zooms
   * IN past 100% (a two-node graph blown up to 250% looks broken), so a small graph just sits centred.
   * The builder leaves this off: you author from the top down, and a jump on mount would fight that.
   */
  fitOnMount?: boolean;
  /**
   * A selector for the element to open centred on, at 100% — instead of framing the whole graph. For a canvas
   * that IS its section (the contact's whole sequence + history), "where are they now" is the point; a fit
   * of everything would be illegibly small. Also adds a "Now" control that brings it back. Falls back to
   * `fitOnMount` behaviour when nothing matches.
   */
  focusSelector?: string;
  /**
   * Scroll like a document: a pan (drag, wheel, trackpad) or zoom stops at the content's edges (with a little
   * room, {@link PAN_EDGE_PX}) instead of sliding off into empty canvas — content taller / wider than the
   * viewport scrolls edge to edge; content that fits stays inside it.
   */
  boundPan?: boolean;
}

/**
 * A pannable, zoomable viewport. Children are rendered in a transformed layer (translate + scale,
 * origin top-left). Drag empty space to pan; scroll to zoom toward the cursor; zoom buttons in the
 * corner. Pointer-downs on buttons / inputs / `[data-no-pan]` elements don't start a pan, so the
 * nodes stay interactive. Mechanics mirror the Helm/touch graph viewports.
 */
export function SequencePanZoom({
  children,
  wheelPan = true,
  fitOnMount = false,
  focusSelector,
  boundPan = false,
}: PanZoomProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The rep has touched the canvas (pressed anywhere in it, or wheeled it): from then on the view is theirs.
  const touchedRef = useRef(false);
  const [view, setView] = useState<View>(INITIAL);
  const dots = dotGridAt(view.scale);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  // Zoom is a modifier gesture: hold Z (or Ctrl/Cmd) while scrolling. Plain scroll / two-finger pans.
  const zHeldRef = useRef(false);

  // Every view change goes through here: with `boundPan`, the content scrolls edge to edge and no further.
  // Reads sizes off refs, so it's stable and safe inside the wheel / drag listeners.
  const clampView = useCallback(
    (v: View): View => {
      const rect = viewportRef.current?.getBoundingClientRect();
      const content = contentRef.current;
      if (!boundPan || rect == null || content == null) return v;
      // Per axis: content bigger than the viewport → its edges may reach the viewport's (plus the pad), no
      // further; content that fits → it stays wholly inside.
      const clampAxis = (pos: number, size: number, viewport: number) =>
        size + 2 * PAN_EDGE_PX > viewport
          ? Math.min(PAN_EDGE_PX, Math.max(viewport - size - PAN_EDGE_PX, pos))
          : Math.max(PAN_EDGE_PX, Math.min(viewport - size - PAN_EDGE_PX, pos));
      return {
        ...v,
        x: clampAxis(v.x, content.scrollWidth * v.scale, rect.width),
        y: clampAxis(v.y, content.scrollHeight * v.scale, rect.height),
      };
    },
    [boundPan],
  );

  useEffect(() => {
    const isTyping = (t: EventTarget | null) =>
      t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    const down = (e: KeyboardEvent) => {
      // Bare Z is the zoom modifier; ignore Cmd/Ctrl+Z (undo) so the ref doesn't stick if the browser
      // swallows the keyup for the combo.
      if (e.key.toLowerCase() === 'z' && !e.metaKey && !e.ctrlKey && !isTyping(e.target)) zHeldRef.current = true;
    };
    const up = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'z') zHeldRef.current = false;
    };
    const blur = () => {
      zHeldRef.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  // Wheel / two-finger: PAN (unless `wheelPan` is off — then the page scrolls); ZOOM (anchored to the
  // cursor) only while Z or Ctrl/Cmd is held.
  useEffect(() => {
    const el = viewportRef.current;
    if (el == null) return;
    const onWheel = (e: WheelEvent) => {
      const zooming = zHeldRef.current || e.ctrlKey || e.metaKey;
      // Embedded in a scrolling page: let a plain wheel fall through to the page (see `wheelPan`). Zoom is
      // an explicit modifier gesture, so it still claims the event on every surface.
      if (!zooming && !wheelPan) return;
      e.preventDefault();
      touchedRef.current = true;
      if (zooming) {
        const rect = el.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setView((v) => {
          const scale = clampScale(v.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
          const k = scale / v.scale;
          return clampView({ scale, x: cx - k * (cx - v.x), y: cy - k * (cy - v.y) });
        });
      } else {
        setView((v) => clampView({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [wheelPan, clampView]);

  // Drag-to-pan via window listeners (robust across the node cards).
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d == null) return;
      setView((v) => clampView({ ...v, x: d.ox + (e.clientX - d.sx), y: d.oy + (e.clientY - d.sy) }));
    };
    const up = () => {
      setDragging(false);
      dragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging, clampView]);

  const onPointerDown = (e: React.PointerEvent) => {
    touchedRef.current = true;
    if (e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('button, input, textarea, a, select, [data-no-pan]')) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
    setDragging(true);
  };

  const zoomBy = useCallback(
    (factor: number) => {
      const rect = viewportRef.current?.getBoundingClientRect();
      const cx = rect ? rect.width / 2 : 0;
      const cy = rect ? rect.height / 2 : 0;
      setView((v) => {
        const scale = clampScale(v.scale * factor);
        const k = scale / v.scale;
        return clampView({ scale, x: cx - k * (cx - v.x), y: cy - k * (cy - v.y) });
      });
    },
    [clampView],
  );

  // Fit the whole graph in view (like the journey's "fit to screen"). `scrollWidth/Height` are the
  // content's UNSCALED layout extent (CSS transforms don't affect them), so we can size + center it.
  const fit = useCallback((maxScale = MAX_SCALE, minScale = MIN_SCALE) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const content = contentRef.current;
    if (rect == null || content == null) return;
    const pad = 40;
    const contentW = content.scrollWidth + pad * 2;
    const contentH = content.scrollHeight + pad * 2;
    if (contentW <= 0 || contentH <= 0) return;
    const natural = clampScale(Math.min(rect.width / contentW, rect.height / contentH) * 0.95);
    const scale = Math.max(minScale, Math.min(maxScale, natural));
    // Centring assumes the content FITS. Once the scale is clamped up to a floor it no longer does, and a
    // centred view puts the start of the sequence off the top-left — on a canvas whose wheel deliberately
    // scrolls the page rather than panning, that start is then genuinely hard to find. So anchor instead:
    // never offset a dimension the content overflows.
    const offset = (viewport: number, content: number) =>
      content * scale <= viewport ? (viewport - content * scale) / 2 + pad * scale : pad * scale;
    setView({ x: offset(rect.width, contentW), y: offset(rect.height, contentH), scale });
  }, []);

  // Centre `focusSelector`'s element at 100% (see FOCUS_AT). Measured off the live DOM: the element's offset
  // inside the content, un-scaled by the current view, so it works from any pan / zoom. False if nothing matches.
  const focus = useCallback((): boolean => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    const el = focusSelector != null ? content?.querySelector(focusSelector) : null;
    if (viewport == null || content == null || el == null) return false;
    const vr = viewport.getBoundingClientRect();
    const cr = content.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    // The focus group's horizontal extent: the point plus anything marked as belonging with it.
    const extras = [...content.querySelectorAll(FOCUS_EXTRA_SELECTOR)].map((x) => x.getBoundingClientRect());
    const groupLeft = Math.min(er.left, ...extras.map((r) => r.left));
    const groupRight = Math.max(er.right, ...extras.map((r) => r.right));
    setView((v) => {
      const ex = (er.left + er.width / 2 - cr.left) / v.scale;
      const ey = (er.top + er.height / 2 - cr.top) / v.scale;
      // Place the point at FOCUS_AT, then nudge right just enough that the group's left edge stays on screen
      // (and left again if that pushed its right edge off — a group wider than the viewport keeps its start).
      const left = (groupLeft - cr.left) / v.scale - ex;
      const right = (groupRight - cr.left) / v.scale - ex;
      let x = vr.width * FOCUS_AT.x - ex;
      x = Math.max(x, FOCUS_EDGE_PX - (ex + left));
      x = Math.min(x, Math.max(FOCUS_EDGE_PX - (ex + left), vr.width - FOCUS_EDGE_PX - (ex + right)));
      return clampView({ scale: 1, x, y: vr.height * FOCUS_AT.y - ey });
    });
    return true;
  }, [focusSelector, clampView]);

  // The opening frame: on `focusSelector`, else a fit. Never past 100% (a two-node graph blown up looks
  // broken) and never below MOUNT_FIT_MIN_SCALE — a wide branching graph squeezed into a detail-panel card goes
  // illegible long before it goes fully visible, so past that floor it opens readable and centred and the rep
  // zooms out if they want the whole shape.
  const frame = useCallback(() => {
    if (focusSelector != null && focus()) return;
    if (fitOnMount || focusSelector != null) fit(1, MOUNT_FIT_MIN_SCALE);
  }, [focusSelector, focus, fitOnMount, fit]);
  // Layout effect, not a plain one: measuring needs layout but not paint, so the first painted frame is
  // already where it should be — no visible jump from the top-left 100% view. Runs once (the ref).
  const framedRef = useRef(false);
  useLayoutEffect(() => {
    if (framedRef.current) return;
    framedRef.current = true;
    frame();
  }, [frame]);
  // Content that arrives after the opening frame — the history column loading above the graph — shifts
  // everything below it, so re-frame on every resize until the rep touches the canvas. Never after: a view
  // they've moved, or a step editor they've opened, is never re-framed under them.
  useEffect(() => {
    const content = contentRef.current;
    if (content == null || (focusSelector == null && !fitOnMount)) return;
    let initial = true;
    const observer = new ResizeObserver(() => {
      // The first callback reports the size the opening frame was measured at.
      if (initial) {
        initial = false;
        return;
      }
      if (!touchedRef.current) frame();
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [frame, focusSelector, fitOnMount]);

  return (
    <div
      ref={viewportRef}
      onPointerDown={onPointerDown}
      onScroll={(e) => {
        // The viewport is TRANSFORM-panned, never scrolled — but an autofocus inside (e.g. a TipTap
        // step editor that opens off to the right of a wide switch fan) can `scrollIntoView` and nudge
        // it, jumping the view. Snap it straight back so clicking a node never shifts the canvas.
        if (e.currentTarget.scrollLeft !== 0 || e.currentTarget.scrollTop !== 0) {
          e.currentTarget.scrollLeft = 0;
          e.currentTarget.scrollTop = 0;
        }
      }}
      className={cn(
        'relative size-full overflow-hidden bg-slate-50 dark:bg-neutral-900',
        // `touch-none` claims touch gestures for panning — same trap as the wheel when embedded, so it's
        // tied to the same switch: an embedded canvas lets a touch drag scroll the page.
        wheelPan && 'touch-none',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
      )}>
      {/* Dot grid, tied to the pan/zoom so it reads as an infinite canvas (see dotGridAt). */}
      <div
        data-dot-grid
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle, rgba(120,130,150,0.35) ${dots.radiusPx}px, transparent ${dots.radiusPx}px)`,
          backgroundSize: `${dots.gapPx}px ${dots.gapPx}px`,
          backgroundPosition: `${view.x + dots.offsetPx}px ${view.y + dots.offsetPx}px`,
        }}
      />
      <div
        ref={contentRef}
        className="absolute left-0 top-0 origin-top-left"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}>
        {children}
      </div>

      <div
        className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm dark:border-gray-700 dark:bg-slate-900"
        onPointerDown={(e) => e.stopPropagation()}>
        <Button size="xs" text icon={<General.Minus />} tooltip="Zoom out" onClick={() => zoomBy(1 / 1.2)} />
        <Button size="xs" text tooltip="Reset view · hold Z (or Ctrl/⌘) + scroll to zoom" onClick={() => setView(INITIAL)}>
          {Math.round(view.scale * 100)}%
        </Button>
        <Button size="xs" text icon={<General.Plus />} tooltip="Zoom in" onClick={() => zoomBy(1.2)} />
        <Button size="xs" text icon={<General.Maximize01 />} tooltip="Fit to screen" onClick={() => fit()} />
        {focusSelector != null && (
          <Button size="xs" text icon={<Time.Clock />} tooltip="Back to where they are now" onClick={() => focus()}>
            Now
          </Button>
        )}
      </div>
    </div>
  );
}
